import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Box, Typography, Alert, Stack, CircularProgress, Chip, FormGroup,
    FormControlLabel, Checkbox, Table, TableHead, TableRow, TableCell,
    TableBody, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../services/api';
import ServerPickerInline from './ServerPickerInline';

const RECORD_TYPES = ['A', 'AAAA', 'MX', 'NS', 'TXT', 'CNAME', 'SOA', 'SRV'];
const DEFAULT_TYPES = ['A', 'AAAA', 'MX', 'NS', 'TXT', 'CNAME'];

const STATUS_CHIP = {
    ok:        { label: 'OK',       color: 'success' },
    nxdomain:  { label: 'NXDOMAIN', color: 'default' },
    noanswer:  { label: 'Sem resposta', color: 'default' },
    timeout:   { label: 'Timeout',  color: 'warning' },
    error:     { label: 'Erro',     color: 'error' },
};

/**
 * Lookup de records DNS — A/AAAA/MX/NS/TXT/CNAME/SOA/SRV — contra
 * os nós configurados ou DNS público.
 */
export default function DnsRecordsLookup({ open, onClose, inline = false }) {
    const [domain, setDomain] = useState('');
    const [types, setTypes] = useState(DEFAULT_TYPES);
    const [picker, setPicker] = useState({ node_ids: [], public_ids: [], custom_servers: [] });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const reset = () => {
        setDomain('');
        setTypes(DEFAULT_TYPES);
        setResult(null);
        setError(null);
        setLoading(false);
    };
    const handleClose = () => {
        if (loading) return;
        reset();
        onClose && onClose();
    };

    const toggleType = (t) => {
        setTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
    };

    const run = async () => {
        const d = domain.trim().toLowerCase();
        if (!d || !d.includes('.')) {
            setError('Domínio inválido');
            return;
        }
        if (types.length === 0) {
            setError('Selecione pelo menos 1 tipo de record');
            return;
        }
        const totalSel = (picker.node_ids?.length || 0) + (picker.public_ids?.length || 0) + (picker.custom_servers?.length || 0);
        if (totalSel === 0) {
            setError('Selecione pelo menos 1 servidor DNS');
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const res = await api.post('/dns/node/lookup', {
                domain: d, types,
                node_ids: picker.node_ids,
                public_ids: picker.public_ids,
                custom_servers: picker.custom_servers,
            });
            if (res.data?.error) {
                setError(res.data.error);
            } else {
                setResult(res.data);
            }
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    const body = (
        <>
                {!inline && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Consulta múltiplos tipos de records DNS (A, AAAA, MX, NS, TXT, etc)
                        contra os nós configurados ou contra os DNS públicos.
                    </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth size="small" label="Domínio"
                        placeholder="ex.: ipv7.com.br"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } }}
                        disabled={loading}
                        autoFocus
                    />
                    <Button
                        variant="contained" color="primary" onClick={run}
                        disabled={loading || !domain.trim()} sx={{ minWidth: 110 }}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Consultar'}
                    </Button>
                </Stack>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Tipos de record
                    </Typography>
                    <FormGroup row sx={{ gap: 0.5 }}>
                        {RECORD_TYPES.map((t) => (
                            <FormControlLabel
                                key={t}
                                control={<Checkbox size="small" checked={types.includes(t)} onChange={() => toggleType(t)} disabled={loading} />}
                                label={<Typography variant="body2">{t}</Typography>}
                            />
                        ))}
                    </FormGroup>
                </Box>

                <ServerPickerInline value={picker} onChange={setPicker} disabled={loading} defaultPublic={false} />

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {result && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            <strong>{result.domain}</strong> — {result.servers.length} servidor(es), {result.types.length} tipo(s)
                        </Typography>

                        {result.servers.map((srv, idx) => {
                            // Conta status agregado por servidor
                            const okCount = Object.values(srv.results).filter((r) => r.status === 'ok').length;
                            const totalRecords = Object.values(srv.results).reduce((acc, r) => acc + (r.records?.length || 0), 0);
                            return (
                                <Accordion key={idx} defaultExpanded={idx === 0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {srv.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                                {srv.server}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={`${okCount}/${result.types.length} OK · ${totalRecords} registros`}
                                                color={okCount === result.types.length ? 'success' : okCount > 0 ? 'warning' : 'default'}
                                                variant="outlined"
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 0 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: 80 }}>Tipo</TableCell>
                                                    <TableCell sx={{ width: 110 }}>Status</TableCell>
                                                    <TableCell>Resposta</TableCell>
                                                    <TableCell align="right" sx={{ width: 70 }}>Tempo</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.types.map((t) => {
                                                    const r = srv.results[t] || {};
                                                    const chip = STATUS_CHIP[r.status] || STATUS_CHIP.error;
                                                    return (
                                                        <TableRow key={t}>
                                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{t}</TableCell>
                                                            <TableCell>
                                                                <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                                {r.records && r.records.length > 0 ? (
                                                                    r.records.map((rec, i) => (
                                                                        <div key={i} style={{ wordBreak: 'break-all' }}>{rec}</div>
                                                                    ))
                                                                ) : (
                                                                    <span style={{ color: '#999' }}>{r.error || r.rcode || '—'}</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 12 }}>
                                                                {r.time_ms}ms
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                )}
        </>
    );

    if (inline) return <Box>{body}</Box>;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Lookup de Records DNS</DialogTitle>
            <DialogContent dividers>{body}</DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
